import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";

// ─── Types ─────────────────────────────────────────────
type CleaningRateType = "perHour" | "perDay" | "perWeek" | "perMonth" | "perYear";

// ─── Utility Functions ─────────────────────────────────
export const calculateCleaningHostEarnings = (total: number) => {
  const hostFeePercentage = 0.0;
  const hostsFee = total * hostFeePercentage;
  const hostProfit = parseFloat((total - hostsFee).toFixed(2));
  return { hostProfit, hostsFee };
};

export const calculateCleaningGuestServiceFee = (subtotal: number): number => {
  if (subtotal > 5000) return +(subtotal * 0.1).toFixed(2);
  if (subtotal > 1000) return +(subtotal * 0.09).toFixed(2);
  if (subtotal > 500)  return +(subtotal * 0.08).toFixed(2);
  if (subtotal > 100)  return +(subtotal * 0.07).toFixed(2);
  return +(subtotal * 0.06).toFixed(2);
};

// ─── Core Calculation Logic ────────────────────────────
/*
  perHour  → (rate × hours) + extrasFlat + guestFee   ← extras are ONE-TIME flat
  perDay   → (rate + extras) × days + guestFee         ← extras scale per day
  perWeek  → (rate + extras) × weeks + guestFee        ← extras scale per week
  perMonth → (rate + extras) × months + guestFee       ← extras scale per month
  perYear  → (rate + extras) × years + guestFee        ← extras scale per year
*/
const computeCleaningCost = ({
  rate,
  rateType,
  totalHours = 0,
  totalDays = 0,
  totalWeeks = 0,
  totalMonths = 0,
  totalYears = 0,
  extraCharges = 0,
  discountPercent = 0,
}: {
  rate: number;
  rateType: CleaningRateType;
  totalHours?: number;
  totalDays?: number;
  totalWeeks?: number;
  totalMonths?: number;
  totalYears?: number;
  extraCharges?: number;
  discountPercent?: number;
}) => {
  let baseServiceCost = 0;

  if (rateType === "perHour") {
    // perHour: base scales with hours, extras are flat one-time
    baseServiceCost = (rate * totalHours) + extraCharges;
  } else {
    // perDay/Week/Month/Year: base + extras both scale with duration
    const rateWithExtras = rate + extraCharges;
    switch (rateType) {
      case "perDay":   baseServiceCost = rateWithExtras * totalDays;   break;
      case "perWeek":  baseServiceCost = rateWithExtras * totalWeeks;  break;
      case "perMonth": baseServiceCost = rateWithExtras * totalMonths; break;
      case "perYear":  baseServiceCost = rateWithExtras * totalYears;  break;
    }
  }

  const discountedServiceCost = baseServiceCost * (1 - discountPercent);
  const guestsFee = calculateCleaningGuestServiceFee(discountedServiceCost);
  const totalCharged = +(discountedServiceCost + guestsFee).toFixed(2);
  const { hostProfit, hostsFee } = calculateCleaningHostEarnings(discountedServiceCost);
  const platformProfit = +(guestsFee + hostsFee).toFixed(2);

  return {
    baseServiceCost,
    discountedServiceCost,
    totalServiceCost: discountedServiceCost,
    guestsFee,
    hostProfit,
    hostsFee,
    platformProfit,
    totalCharged,
    fullCost: discountedServiceCost + guestsFee,
  };
};

// ─── Earnings Calculation ─────────────────────────────
export const calculateCleaningEarnings = createAsyncThunk(
  "cleaningServiceFees/calculateCleaningEarnings",
  async ({
    rate,
    rateType,
    totalHours = 0,
    totalDays = 0,
    totalWeeks = 0,
    totalMonths = 0,
    totalYears = 0,
    extraCharges = 0,
    discountApplied = {},
    creditsAvailable = 0,
  }: {
    rate: number;
    rateType: CleaningRateType;
    totalHours?: number;
    totalDays?: number;
    totalWeeks?: number;
    totalMonths?: number;
    totalYears?: number;
    extraCharges?: number;
    discountApplied?: {
      is5?: boolean;
      is10?: boolean;
      is25?: boolean;
      is50?: boolean;
      is80?: boolean;
    };
    creditsAvailable?: number;
  }) => {
    let discountPercent = 0;
    if (discountApplied.is80)      discountPercent = 0.8;
    else if (discountApplied.is50) discountPercent = 0.5;
    else if (discountApplied.is25) discountPercent = 0.25;
    else if (discountApplied.is10) discountPercent = 0.1;
    else if (discountApplied.is5)  discountPercent = 0.05;

    const computed = computeCleaningCost({
      rate, rateType,
      totalHours, totalDays, totalWeeks, totalMonths, totalYears,
      extraCharges, discountPercent,
    });

    const creditsUsed = Math.min(creditsAvailable, computed.discountedServiceCost);
    const serviceAfterCredits = computed.discountedServiceCost - creditsUsed;
    const totalCharged = +(serviceAfterCredits + computed.guestsFee).toFixed(2);

    return {
      ...computed,
      rateType,
      creditsAvailable,
      creditsUsed,
      totalCharged,
      fullCost: computed.discountedServiceCost + computed.guestsFee,
    };
  }
);

// ─── Calculate Final Prices For All Listings ──────────
export const calculateFinalPricesForAllCleaningListings = createAsyncThunk(
  "cleaningServiceFees/calculateFinalPricesForAllCleaningListings",
  async (
    {
      totalHours = 0,
      totalDays = 0,
      totalWeeks = 0,
      totalMonths = 0,
      totalYears = 0,
    }: {
      totalHours?: number;
      totalDays?: number;
      totalWeeks?: number;
      totalMonths?: number;
      totalYears?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const snapshot = await getDocs(collection(db, "cleaning"));
      const results: Record<string, { totalCharged: number; fullCost: number }> = {};

      snapshot.forEach((docSnap) => {
        const listing = docSnap.data();
        const listingId = docSnap.id;

        const rate: number = listing.rate || 0;
        const rateType: CleaningRateType = listing.rateType || "perDay";
        const extraCharges: number = listing.extraCharges || 0;

        // ─── fix: read discountApplied (matches what CleaningForm saves) ───
        const discounts = listing.discountApplied || listing.discounts || {};
        let discountPercent = 0;
        if (discounts.is80)      discountPercent = 0.8;
        else if (discounts.is50) discountPercent = 0.5;
        else if (discounts.is25) discountPercent = 0.25;
        else if (discounts.is10) discountPercent = 0.1;
        else if (discounts.is5)  discountPercent = 0.05;

        // fallback duration derivation
        const effectiveHours  = totalHours  || totalDays * 8;
        const effectiveDays   = totalDays   || Math.ceil(totalHours / 8);
        const effectiveWeeks  = totalWeeks  || Math.ceil(effectiveDays / 7);
        const effectiveMonths = totalMonths || Math.ceil(effectiveWeeks / 4);
        const effectiveYears  = totalYears  || Math.ceil(effectiveMonths / 12);

        const computed = computeCleaningCost({
          rate, rateType,
          totalHours: effectiveHours,
          totalDays: effectiveDays,
          totalWeeks: effectiveWeeks,
          totalMonths: effectiveMonths,
          totalYears: effectiveYears,
          extraCharges,
          discountPercent,
        });

        results[listingId] = {
          totalCharged: computed.totalCharged,
          fullCost: computed.fullCost,
        };
      });

      return results;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ─── Slice ─────────────────────────────────────────────
const cleaningServiceFeeSlice = createSlice({
  name: "cleaningServiceFees",
  initialState: {
    totalCharged: 0,
    platformProfit: 0,
    creditsAvailable: 0,
    hostsFee: 0,
    baseServiceCost: 0,
    discountedServiceCost: 0,
    totalServiceCost: 0,
    guestsFee: 0,
    hostProfit: 0,
    creditsUsed: 0,
    fullCost: 0,
    finalPricesByListing: {} as Record<string, { totalCharged: number; fullCost: number }>,
    monthlyEarningsData: {} as Record<string, number>,
    monthlyEarnings: {},
  },
  reducers: {
    resetCleaningEarnings: (state) => {
      Object.assign(state, cleaningServiceFeeSlice.getInitialState());
    },
    clearCleaningFinalPrices: (state) => {
      state.finalPricesByListing = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(calculateCleaningEarnings.fulfilled, (state, action) => {
      Object.assign(state, action.payload);
    });
    builder.addCase(calculateFinalPricesForAllCleaningListings.fulfilled, (state, action) => {
      state.finalPricesByListing = action.payload;
    });
  },
});

export const { resetCleaningEarnings, clearCleaningFinalPrices } =
  cleaningServiceFeeSlice.actions;
export default cleaningServiceFeeSlice.reducer;